import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Route as RouteIcon, User, TriangleAlert } from 'lucide-react';
import { SLOT_LABEL, STATUS_LABEL, formatDay, formatDateTime, todayUk } from '@nu/shared';
import { api } from '../../api/client.js';
import { useAutoRefresh } from '../../useAutoRefresh.js';
import RouteMap from '../../components/RouteMap.jsx';
import { LEAVE_LABEL, addDays, useBookingEditor, useBookingRefs } from '../bookings/shared.jsx';
import { problem } from '../bookings/util.js';
import { drive, minutes } from './format.js';

// Plan the day (office): each team's jobs for a date, put in driving order by
// our routing server. Optimise saves the order; it's what the engineers see
// in the app. If the day's jobs, postcodes, slots or homes change, the plan
// shows as out of date until it's optimised again.

const isoDay = (v) => (/^\d{4}-\d{2}-\d{2}$/.test(v || '') ? v : null);
const dayName = (day) => new Intl.DateTimeFormat('en-GB', { weekday: 'long', timeZone: 'UTC' }).format(new Date(`${day}T12:00:00Z`));

export default function PlanTheDay({ me }) {
  const canEdit = me.capabilities.includes('page.plan.edit');
  const [params, setParams] = useSearchParams();
  const day = isoDay(params.get('day')) || todayUk();
  const setDay = (d) => setParams({ day: d }, { replace: true });
  const { types, teams, colours } = useBookingRefs();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null); // team id being optimised
  const busyRef = useRef(null);
  busyRef.current = busy;
  const [focus, setFocus] = useState(null); // team id to show alone on the map

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await api(`/plans?day=${day}`));
    } catch (e) {
      setError(problem(e));
    }
  }, [day]);
  useEffect(() => { setData(null); setFocus(null); load(); }, [load]);
  // Others' changes (another planner optimising, a booking moved) show up;
  // not while this page is optimising.
  useAutoRefresh(() => { if (busyRef.current === null) load(); });

  const { notice, setNotice, openBooking, modal } = useBookingEditor({ canEdit, types, teams, reload: load });

  const optimise = async (td) => {
    setBusy(td.team.id);
    setNotice(null);
    try {
      const d = await api(`/plans/${td.team.id}/${day}`, { method: 'POST' });
      setData(d);
      const n = d.teamDays.find((t) => t.team.id === td.team.id)?.plan?.slotsSet || 0;
      setNotice({ tone: 'ok', text: `${td.team.name} planned for ${formatDay(day)}: starting at the furthest job and working back towards home.${n ? ` ${n} slot${n === 1 ? '' : 's'} set (AM the far end, PM coming home).` : ''} The engineers see this order in the app.` });
    } catch (e) {
      setNotice({ tone: 'bad', text: problem(e) });
    } finally {
      setBusy(null);
    }
  };

  const targetOf = (teamId) => teams.find((t) => t.id === teamId)?.target?.value ?? null;

  // Everything on the map: each team's route in its colour, stops numbered.
  const map = useMemo(() => {
    const out = { routes: [], stops: [], homes: [] };
    for (const td of data?.teamDays || []) {
      if (focus && td.team.id !== focus) continue;
      const colour = colours.get(td.team.id) || '#0B2340';
      const current = td.plan?.current;
      if (current) {
        out.routes.push({ key: `r${td.team.id}`, colour, line: td.plan.line });
        for (const v of td.plan.vans.filter((x) => !x.noHome)) {
          out.routes.push({ key: `o${td.team.id}${v.personId}`, colour, line: v.lineOut, dashed: true });
          out.routes.push({ key: `b${td.team.id}${v.personId}`, colour, line: v.lineBack, dashed: true });
        }
      }
      td.jobs.forEach((j) => out.stops.push({
        key: `s${j.id}`, coord: j.coord, label: j.seq, colour,
        title: `${td.team.name} · ${j.seq}. ${SLOT_LABEL[j.slot] || 'No slot'} · ${j.postcode} · ${j.reference}`,
        onClick: () => openBooking(j.id),
      }));
      // Homes only matter on a day with work (they are where the vans start).
      // Where each van starts: its 02:00 spot once planned, else home.
      if (td.jobs.length) {
        td.members.forEach((m) => {
          const v = current ? td.plan.vans.find((x) => x.personId === m.id) : null;
          const fromVan = v?.startFrom?.kind === 'van';
          out.homes.push({ key: `h${td.team.id}${m.id}`, coord: fromVan ? v.home : m.coord, colour, title: `${m.name}'s start` });
        });
      }
    }
    (data?.unassigned || []).forEach((j) => out.stops.push({ key: `u${j.id}`, coord: j.coord, label: '?', colour: '#8a94a3', title: `Not on a team · ${j.postcode} · ${j.reference}`, onClick: () => openBooking(j.id) }));
    return out;
  }, [data, colours, focus, openBooking]);

  return (
    <div className="stack">
      <section className="card">
        <div className="page-head">
          <div>
            <h1>Plan the day</h1>
            <p className="muted">Each team&apos;s jobs in driving order: Optimise starts the day at the furthest job and works back towards home, setting the slots (AM the far end, PM coming home; fixed slots like AM must attend stay). Optimise saves the order the engineers see in the app.</p>
          </div>
        </div>
        {notice && <p className={`notice-line notice-${notice.tone}`} role="status">{notice.text}</p>}
        <div className="filters">
          <div className="week-nav" role="group" aria-label="Day">
            <button type="button" className="btn icon-only" aria-label="Previous day" onClick={() => setDay(addDays(day, -1))}><ChevronLeft size={18} aria-hidden="true" /></button>
            <button type="button" className="btn" onClick={() => setDay(todayUk())}>Today</button>
            <button type="button" className="btn icon-only" aria-label="Next day" onClick={() => setDay(addDays(day, 1))}><ChevronRight size={18} aria-hidden="true" /></button>
            <input type="date" className="filter-date" value={day} aria-label="Day" onChange={(e) => isoDay(e.target.value) && setDay(e.target.value)} />
            <span className="week-label">{dayName(day)} {formatDay(day)}</span>
          </div>
          {data?.teamDays?.length > 1 && (
            <select value={focus || ''} onChange={(e) => setFocus(e.target.value ? Number(e.target.value) : null)} aria-label="Team on the map">
              <option value="">All teams on the map</option>
              {data.teamDays.map((td) => <option key={td.team.id} value={td.team.id}>{td.team.name}</option>)}
            </select>
          )}
        </div>
      </section>

      {error && <section className="card"><div className="empty"><p>{error}</p><button type="button" className="btn" onClick={load}>Try again</button></div></section>}
      {!error && !data && <section className="card"><p className="muted">Loading…</p></section>}

      {data && (
        <>
          <section className="card map-card">
            <RouteMap {...map} height={420} fullscreen />
            <p className="small muted">Numbers are the order of the day; dashed lines are each van&apos;s drive from home and back. Click a stop to open the booking.</p>
          </section>

          {data.unassigned.length > 0 && (
            <section className="card">
              <p className="notice-line notice-warn">
                {data.unassigned.length} booking{data.unassigned.length === 1 ? ' is' : 's are'} on this day but not on a team. Put {data.unassigned.length === 1 ? 'it' : 'them'} on a team in the <Link to="/bookings/calendar">Bookings calendar</Link>.
              </p>
            </section>
          )}

          {data.teamDays.map((td) => {
            const colour = colours.get(td.team.id);
            const target = targetOf(td.team.id);
            const current = td.plan?.current;
            const notFound = td.jobs.filter((j) => !j.coord);
            const noHome = td.members.filter((m) => !m.coord);
            const vanOf = (id) => (current ? td.plan.vans.find((v) => v.personId === id) : null);
            return (
              <section key={td.team.id} className="card plan-team" style={{ '--team': colour }}>
                <div className="page-head">
                  <div>
                    <h2>{td.team.name} <span className="muted small">{td.jobs.length} job{td.jobs.length === 1 ? '' : 's'}{target !== null ? ` of ${target}` : ''}</span></h2>
                    <ul className="crew crew-inline">
                      {td.members.map((m) => (
                        <li key={m.id}>
                          <User size={14} aria-hidden="true" />{m.name}
                          {m.leave && <span className="tag tag-leave">{LEAVE_LABEL[m.leave]}</span>}
                          {m.van && <span className="muted small">&nbsp;· {m.van.registration}</span>}
                          {vanOf(m.id) && !vanOf(m.id).noHome && <span className="muted small">&nbsp;· {minutes(vanOf(m.id).toFirst.seconds)} to the first job, {minutes(vanOf(m.id).fromLast.seconds)} back</span>}
                          {vanOf(m.id)?.startFrom && (
                            <span className="small start-from">
                              {vanOf(m.id).startFrom.kind === 'van'
                                ? <>Starts where the van was parked overnight</>
                                : <>Starts from home (van not on Samsara or no 02:00 position)</>}
                            </span>
                          )}
                        </li>
                      ))}
                      {!td.members.length && <li className="muted">Nobody in this team on this day</li>}
                    </ul>
                  </div>
                  <div className="head-actions">
                    {canEdit && td.jobs.length > 0 && (
                      <button type="button" className={`btn with-icon${current ? '' : ' primary'}`} disabled={busy !== null} onClick={() => optimise(td)}>
                        <RouteIcon size={18} aria-hidden="true" /> {busy === td.team.id ? 'Planning…' : current ? 'Optimise again' : 'Optimise'}
                      </button>
                    )}
                  </div>
                </div>

                {td.jobs.length > 0 && (
                  <p className={`small ${current ? 'muted' : 'warn-text'}`}>
                    {current
                      ? `Planned by ${td.plan.plannedBy} on ${formatDateTime(td.plan.plannedAt)}. Driving between jobs ${minutes(td.plan.totals?.jobs?.seconds)}.`
                      : td.plan
                        ? 'Out of date: the jobs, postcodes, slots or homes changed since this was planned. The app shows slot order until it is optimised again.'
                        : 'Not planned yet. The app shows slot order (AM, all day, PM) until it is optimised.'}
                  </p>
                )}
                {(target !== null && td.jobs.length > target) && <p className="small warn-text"><TriangleAlert size={14} aria-hidden="true" /> More jobs than the team&apos;s target of {target}.</p>}
                {notFound.length > 0 && <p className="small warn-text"><TriangleAlert size={14} aria-hidden="true" /> Postcode not found for {notFound.map((j) => `${j.reference} (${j.postcode})`).join(', ')}: {notFound.length === 1 ? 'it goes' : 'they go'} at the end. Check the postcode on the booking.</p>}
                {noHome.length > 0 && <p className="small warn-text"><TriangleAlert size={14} aria-hidden="true" /> No home postcode for {noHome.map((m) => m.name).join(', ')}: add it in Match-Ups so their drive is counted.</p>}

                {td.jobs.length === 0
                  ? <p className="muted">No jobs for {td.team.name} on this day.</p>
                  : (
                    <div className="table-scroll">
                      <table className="table roster clickable plan-list">
                        <thead><tr><th>#</th><th>Slot</th><th>Ref</th><th>Postcode</th><th>Address</th><th>Status</th><th>Drive from last stop</th></tr></thead>
                        <tbody>
                          {td.jobs.map((j) => (
                            <tr key={j.id} tabIndex={0} onClick={() => openBooking(j.id)} onKeyDown={(e) => { if (e.key === 'Enter') openBooking(j.id); }}>
                              <td data-label="#"><span className="seq" style={{ background: colour }}>{j.seq}</span></td>
                              <td data-label="Slot">{SLOT_LABEL[j.slot] || <span className="muted">–</span>}{j.slot && j.slotAuto && <span className="slot-auto" title="Set by Optimise">auto</span>}</td>
                              <td data-label="Ref"><strong>{j.reference}</strong></td>
                              <td data-label="Postcode">{j.postcode}{!j.coord && <span className="tag">Not found</span>}</td>
                              <td data-label="Address" className="wrap">{[j.addressLine1, j.town].filter(Boolean).join(', ')}</td>
                              <td data-label="Status"><span className={`status status-${j.status}`}>{STATUS_LABEL[j.status]}</span></td>
                              <td data-label="Drive">{j.fromPrevious ? drive(j.fromPrevious) : <span className="muted">{j.seq === 1 && current ? 'First job' : '–'}</span>}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
              </section>
            );
          })}
          {!data.teamDays.length && <section className="card"><p className="muted">No teams yet. Set them up in Match-Ups.</p></section>}
        </>
      )}
      {modal}
    </div>
  );
}
